/**
 * English strings. Sentence case everywhere; a control names exactly what happens when it is used.
 *
 * Identifiers are absent by design — kiosk ids, API status strings and error codes are not
 * language. They are read aloud between a floor and a terminal, quoted to support and grepped in
 * logs, so translating one makes it unmatchable.
 */
const en = {
  /**
   * Owned by `app/error-handler.ts`'s render-level boundary — the one message shown when a render
   * threw and there is nothing else on screen to point at. Deliberately generic: whatever actually
   * broke is in the console (`app/error-handler.ts`'s own `reportError`), not this string, because
   * an operator reading this has no use for a stack trace and every use for "reload and it's fine."
   */
  app: {
    renderError:
      "Something went wrong showing this screen. Reload the page — if it happens again, tell IT what you were doing.",
  },
  /**
   * Strings owned by `src/shared/components/*.vue` — the handful of components every workspace
   * shares. `ConfirmIrreversible.vue` is the one shared component allowed to carry its own i18n
   * (its callers pass in `title`/`confirmLabel`/`cancelLabel`/`acknowledgement` already translated;
   * `typeToConfirm` is the one instruction the component renders itself).
   */
  shared: {
    confirmIrreversible: {
      typeToConfirm: "Type {phrase} to continue",
    },
  },
  intent: {
    settled: "settled",
    "in-flight": "in flight",
    "needs-human": "needs a human",
    "not-comparable": "not comparable",
  },
  op: {
    float_topup: "float top-up",
    smart_empty: "empty to cash box",
    closeout: "closeout",
    reconcile: "reconcile",
  },
  money: {
    declared: "declared",
    toFloat: "reached the float",
    toCashbox: "to the cash box",
    short: "short",
    /** `difference` is `payoutTotal − expectedAmount`: negative labels `short`, positive labels
     *  `over` — more reached the float than was declared. Never both from the same sign. */
    over: "over",
  },
  queue: { andMore: "and {n} more" },
  signoff: {
    title: "Sign off {date}",
    businessDay: "business day",
    verdict: "verdict being signed",
    cashAccepted: "cash accepted",
    irreversible:
      "No route reverses this. A late credit can still land, and the report will show what changed.",
    confirm: "Sign off {date}",
    cancel: "Cancel",
    notOver: "This day is not over yet. It can be signed off after {time}.",
    heading: "Sign off",
    needBreakGlass: "Enter a Casino from Tenancy to sign off its business day.",
    onlyManager: "Only a manager can sign off a business day.",
    /** Adjacent to the date input itself, not just in the caption below it — some browsers show
     *  this field's year in the Buddhist Era (BE) under a Thai locale, and this action is
     *  insert-only with no route back. Named here so a field reading `2569` next to a caption
     *  reading `2026` reads as "same day, two calendars" rather than a disagreement. */
    dateFieldCalendar:
      "This field may show the Buddhist Era (BE) year — the date confirmed below is always Gregorian (CE).",
    noteLabel: "Note (optional)",
    notePlaceholder: "e.g. night shift, verified against camera footage",
    window: "Window {from}–{to}",
    alreadyHeading: "Signed off",
    signedAt: "Signed {when}",
    signedBy: "By {staff}",
    verdictAtSignoffLabel: "Verdict at sign-off",
    verdictUnknown: "Not recorded in the snapshot",
    submitting: "Signing off…",
    errorAlreadySigned: "This business day has already been signed off.",
    errorNotOver: "This business day is not over yet.",
    errorScope: "This Casino does not match your session. Refresh and try again.",
    errorCasinoRequired: "Choose a Casino before signing off.",
    errorInactive: "This Casino or staff account is inactive.",
    errorUpstream: "Could not reach the reconciliation service. Try again.",
    errorGeneric: "Could not complete that request. Check the details and try again.",
    errorBuddhistYear:
      "{year} looks like a Buddhist Era year — this field wants the Gregorian (CE) year, {ceYear}.",
    errorInvalidDate: "Enter a valid business date.",
    confirmUnavailable: "Sign off unavailable",
    casino: "Casino",
    delta: {
      heading: "Changed since sign-off",
      noChange: "Nothing has changed since sign-off.",
      cannotCompare:
        "This cannot be compared to the signed snapshot — the report's shape has changed since sign-off.",
    },
    stepUp: {
      password: "Your password",
      errorPasswordRequired: "Enter your password to confirm.",
      errorInvalidPassword: "Incorrect password.",
      errorStepUpRequired:
        "For this action your password must be confirmed again. Enter it and retry.",
      errorRateLimited: "This is locked for another {minutes} minutes. It is not your password.",
      errorGeneric: "Could not verify your password. Try again.",
      verifying: "Verifying your password…",
    },
  },
  breakglass: {
    acting: "Acting at {casino}",
    recorded: "platform_admin rights. Every action is recorded against you.",
    leave: "Leave {casino}",
  },
  empty: {
    operations: "No operations today",
    operationsBody: "Authorize one when a machine needs servicing.",
    exceptions: "No open exceptions",
    exceptionsBody: "Every payout exception and stuck cash event has been resolved.",
  },
  error: { rateLimited: "This is locked for another {minutes} minutes. It is not your password." },
  auth: {
    title: "Sign in",
    brandTagline: "Cash operations",
    thesisHeadlineLead: "Every baht must",
    thesisHeadlineEm: "reconcile",
    thesisBody:
      "Count from the machine, weigh it against the wallet, and close the day with confidence.",
    thesisPoint1: "Every cash movement is checked against two sources.",
    thesisPoint2: "A day signs off only when it balances — or the difference is acknowledged.",
    thesisPoint3: "Every action is written to the audit log, with who did it and when.",
    audited: "Every sign-in is written to the audit log.",
    username: "Username",
    password: "Password",
    casinoId: "Casino partition",
    casinoIdHint: "Which partition to sign in to. Leave blank for the platform_admin partition.",
    submit: "Sign in",
    submitting: "Signing in…",
    invalidCredentials: "Incorrect username or password.",
    genericError: "Sign-in failed. Try again.",
    signedIn: "Signed in as {username}. Landing area: {workspace}.",
    /** `app/session-guard.ts`'s own message — a 401 mid-shift, never a bare sign-in form pretending
     *  nothing happened. */
    idleLocked:
      "Locked after 15 minutes without activity. Sign in again to continue where you were.",
    sessionExpired: "Your session has ended. Sign in again to continue.",
  },
  workspace: {
    kiosks: "Kiosks",
    cage: "Cage",
    day: "Business day",
    tenancy: "Tenancy",
    activity: "Activity log",
  },
  role: {
    platform_admin: "Platform admin",
    manager: "Manager",
    technician: "Technician",
    cashier: "Cashier",
  },
  nav: { back: "Back" },
  shell: {
    /** The product name. A brand, so it reads the same in both locales. */
    title: "Kiosk Console",
    workspaces: "Workspaces",
    signOut: "Sign out",
    /** The header menu's accessible name. It holds language and sign-out and never identity —
     *  deliberately not "Account", because it is also the only control on the sign-in screen,
     *  where there is no account yet. */
    menu: "Menu",
    language: "Language",
    /** Each language names itself, in itself. A toggle reading "Thai" in English helps nobody who needs it. */
    en: "English",
    th: "ไทย",
    /** Stands where a Casino would, for the one role that has none. */
    allCasinos: "All Casinos",
    skipToContent: "Skip to content",
  },
  /**
   * The technician workspace: the Kiosk queue, one Kiosk's operations, the float top-up form and
   * the activation code. `kiosks.retired`/`kiosks.neverSeen` mirror `backofficeListKiosks`'s own
   * doc comment on the two states staff most need to see.
   */
  kiosks: {
    heading: "Kiosks",
    needBreakGlass: "Enter a Casino from Tenancy to see its kiosks.",
    retired: "Retired",
    neverSeen: "Never sent a heartbeat",
    healthy: "No action needed",
    allKiosksHeading: "All kiosks",
    operationsFor: "Operations — {kiosk}",
    newOperation: "New operation",
    newOperationOnlyTechnician: "Only a technician can authorize a new operation.",
    retiredReason:
      "This kiosk is retired. It cannot take a new operation or a new activation code.",
    operationsErrorGeneric: "Could not load operations. This list may be missing rows — try again.",
    authorize: {
      title: "Authorize an operation — {kiosk}",
      kind: "Kind",
      declarationHint: "Count what you are loading. The total is calculated, not typed.",
      submit: "Authorize",
      submitting: "Authorizing…",
      cancel: "Cancel",
      errorDenomination: "One of these denominations cannot become float.",
      errorRetired: "This kiosk is retired and cannot take a new operation.",
      errorNotFound: "This kiosk could not be found.",
      errorGeneric: "Could not complete that request. Check the details and try again.",
    },
    operation: {
      expired: "Expired — the kiosk will no longer claim this.",
      cancel: "Withdraw authorization",
      cancelling: "Withdrawing…",
      cancelOnlyTechnician: "Only the technician who authorized this can withdraw it.",
      errorNotCancellable: "The kiosk has already claimed this — it can no longer be withdrawn.",
      close: "Close",
      closeOnlyManager: "Only a manager can close a stranded operation.",
      closeFromDay: "Close a stranded operation from the Business day workspace.",
    },
    activation: {
      button: "Issue activation code",
      title: "Issue an activation code — {kiosk}",
      body: "This creates an activation code for this kiosk. The machine can use the same code every time it starts, until a new code is issued (the old one stops working at once) or the kiosk's credential is revoked. It is shown once and stored hashed — if it is lost, issue a new one.",
      issue: "Issue code",
      issuing: "Issuing…",
      cancel: "Cancel",
      confirm: "I've written it down",
      acknowledge: "I've written down this code. It will not be shown again.",
      expires: "First use before {when} — after that it keeps working until replaced or revoked",
      retired: "This kiosk is retired and cannot be activated.",
      dismiss: "Close without confirming",
      dismissConfirm: "Yes, close without confirming",
      dismissWarning:
        "This code will not be shown again. Click again to close without confirming you wrote it down.",
    },
    revoke: {
      button: "Revoke credential",
      onlyManager: "Only a manager can revoke a kiosk's credential.",
      credentialUnavailable: "This kiosk's credential id is not available yet.",
      title: "Revoke this kiosk's credential — {kiosk}",
      confirm: "Revoke {kiosk}'s credential",
      cancel: "Cancel",
      consequence:
        "This immediately stops {kiosk} from starting new player sessions or moving cash. It can only be re-activated with a fresh activation code.",
      revoking: "Revoking…",
      errorNotFound: "This credential is already gone — there is nothing left to revoke.",
      errorUpstream: "Could not reach the device service. Try again.",
      errorGeneric: "Could not complete that request. Check the details and try again.",
      success: "Credential revoked.",
    },
    register: {
      trigger: "Register kiosk",
      onlyManager: "Only a manager can register a new kiosk.",
      title: "Register a kiosk",
      kioskId: "Kiosk id",
      kioskIdHint:
        "Letters, digits, underscore and hyphen only — this is what goes on the machine.",
      label: "Label",
      labelHint: 'How this kiosk appears in the fleet list, e.g. "Floor 2 west".',
      submit: "Review registration",
      cancel: "Cancel",
      confirmTitle: "Register kiosk — {kiosk}",
      confirmLabel: "Register {kiosk}",
      consequence:
        "This creates the kiosk row every later activation, credential and device token is scoped to. It cannot take a cash operation until a technician activates it.",
      registering: "Registering…",
      errorAlreadyExists: "A kiosk with that id is already registered.",
      errorInvalidId: "A kiosk id may contain only letters, digits, underscore and hyphen.",
      errorCasinoRequired: "Choose a Casino before registering a kiosk.",
      errorUpstream: "Could not reach the kiosk service. Try again.",
      errorGeneric: "Could not complete that request. Check the details and try again.",
      successTitle: "Kiosk registered — {kiosk}",
      successBody:
        "This kiosk exists but cannot pair with a physical machine until it has an activation code.",
      done: "Done",
    },
    tile: {
      neverSeen: "Never seen",
      neverSeenBody: "Registered but has not reported once",
      stale: "Not seen recently",
      noCredential: "No active credential",
      retired: "Retired",
    },
    /** Column headers for the fleet table. There is no `health` VALUE here: `kioskHealth`'s four
     *  answers render as `kiosks.retired`, `kiosks.neverSeen`, `kiosks.tile.stale` and
     *  `kiosks.healthy`, all of which this screen already had. */
    table: { kiosk: "Kiosk", status: "Status", health: "Health" },
    fleet: { reporting: "{n} of {total} reporting" },
  },
  /**
   * The cashier workspace: the withdrawal front and centre, the exception list one click away at
   * `/cage/exceptions`. The withdrawal sits in `ConfirmIrreversible`'s middle tier deliberately —
   * the player is standing there, watched the debit and counts the money, and ADR-0005 says that
   * witness is the control.
   */
  cage: {
    heading: "Cage",
    needBreakGlass: "Enter a Casino from Tenancy to perform a cage withdrawal.",
    withdrawal: {
      heading: "Withdraw",
      playerId: "Player ID",
      amount: "Amount (THB)",
      note: "Note (optional)",
      submit: "Review withdrawal",
      stepsLabel: "Withdrawal steps",
      step1: "Enter details",
      step2: "Review",
      step3: "Dispense",
      quickAmountsLabel: "Common amounts",
      toDispense: "To dispense",
      confirmTitle: "Withdraw {amount} — {player}",
      consequence: "A wallet is debited and notes handed over.",
      confirmLabel: "Withdraw {amount}",
      cancel: "Cancel",
      submitting: "Withdrawing…",
      newWithdrawal: "New withdrawal",
      walletRef: "Wallet reference",
      resultPending: "Still processing.",
      resultPosted: "Paid. Hand over the notes.",
      resultRefused: "The wallet declined this withdrawal. Review the details and try again.",
      /** Names the destination the cashier must actually go to. It used to say "the exception list
       *  below", which pointed at a heading that did not exist by that name (`docs/TH-REVIEW.md`
       *  §2) and stopped being "below" at all once the list moved to `/cage/exceptions` — shown at
       *  the one moment the operator has been told not to retry, so it has to be findable. */
      resultUnresolved:
        "The outcome could not be confirmed. Do not retry — this withdrawal now appears under Payout exceptions for follow-up.",
      resultClosed: "Closed without a wallet write.",
      errorEventMismatch:
        "This withdrawal reference is already in use for a different amount or player. Start a new withdrawal.",
      errorConflict: "This reference is already claimed elsewhere. Start a new withdrawal.",
      errorRace:
        "Another attempt already recorded this withdrawal's outcome. Submit again to see the current state.",
      errorScopeMismatch: "This Casino does not match your session. Refresh and try again.",
      errorInactive: "This Casino or staff account is inactive.",
      errorUpstream: "Could not reach the wallet service. Try again.",
      errorGeneric: "Could not complete that request. Check the details and try again.",
      /** HANDOFF §5: after a 5xx, a timeout or a dropped connection the debit may have posted. */
      errorInDoubt:
        "No answer for the {amount} withdrawal to {player}. The wallet may already have debited it — do not hand over notes yet.",
      inDoubt:
        "Retry sends the same {amount} withdrawal to {player} under the same reference, so the player cannot be debited twice for it. Its details are locked until it answers.",
      retrySame: "Retry this withdrawal",
      setAside: "Set it aside",
      setAsideHint:
        "Only if the player has left. Note reference {eventId} and tell a manager — this withdrawal may already have debited the player.",
      /** `replayed: true` — this request moved no money (HANDOFF §5). Neutral, not an error. */
      replayed:
        "Already paid — this reference was settled on an earlier attempt, so this request moved no money. Do not hand over notes again unless they were not handed over the first time.",
    },
    exceptions: {
      heading: "Payout exceptions",
      subheading:
        "Open across all days — not filtered by date, because an open exception stays open.",
      errorGeneric: "Could not load exceptions. This list may be missing rows — try again.",
      stuckHeading: "Stuck cash events",
      kiosk: "Kiosk {id}",
      cage: "Cage",
      authorized: "authorized",
      dispensed: "dispensed",
      reasonUnknownDispense: "Unknown dispense — count the cassette.",
      reasonRefundUnresolved: "Refund unresolved — check the vendor's record.",
      reasonDebitUnresolved: "Debit unresolved — check the vendor's record for the debit.",
      reasonUnknown: "Reason not yet determined.",
      cageWithdrawalKind: "Cage withdrawal",
      exceptionResolutionKind: "Exception resolution",
      loadMoreError: "Could not load the next page. The rows above are still current.",
      retry: "Try again",
      loadMore: "Load more",
    },
    /**
     * The `/cage` dashboard tile standing where the exception list used to sit. It is the only
     * thing on that screen carrying a figure, so its copy has to survive being wrong: `atLeast`
     * exists because the list is keyset-paged and page one alone cannot say "3" full stop, and
     * `exceptionsError` is the count failing — never the list, which the tile still links to.
     */
    tile: {
      exceptions: "Open exceptions",
      exceptionsBody: "Not filtered by date — a player is owed an answer on each one.",
      exceptionsError: "Could not count open exceptions. Open the list to check.",
      atLeast: "{n}+",
    },
  },
  /**
   * The manager workspace: the day's reconciliation report. The `VerdictBand` on `/day` (captioned
   * `verdict.dayHint`) and `operations.heading`'s own `StateBadge` are two SEPARATE indicators —
   * see `features/day/routes/index.vue`'s own doc comment for why they are never merged.
   */
  day: {
    heading: "Business day",
    dateLabel: "Business date",
    needBreakGlass: "Enter a Casino from Tenancy to see its reconciliation.",
    errorGeneric: "Could not load the reconciliation report. Try again.",
    loading: "Loading…",
    tile: {
      kiosks: "Kiosks not balanced",
      kiosksOf: "of {total} on the floor",
      unresolved: "Unresolved",
      unresolvedBody: "The outcome is not known and will not resolve itself",
      exceptions: "Payout exceptions",
      stuck: "Stuck operations",
      unattributed: "Unattributed operations",
      unattributedBody: "Created with no authorization behind them",
      adjustments: "Wallet adjustments",
      cage: "Cage",
      signoff: "Sign off",
      signoffDone: "Signed off",
      signoffOpen: "Not signed off",
    },
    clear: {
      kiosks: "All {n} kiosks balanced",
      cage: "Cage balanced",
    },
    verdict: {
      dayHint: "Cash accepted against credit issued, and the cage.",
    },
    recon: {
      cashIn: "Cash in",
      cashOut: "Cash out",
      deviceCredited: "Credited at kiosks",
      walletSucceeded: "Wallet credited",
      deviceDispensed: "Dispensed at kiosks",
      walletDebited: "Wallet debited",
      deltaLabel: "Difference",
      balanced: "Balanced",
      off: "Off",
    },
    queue: {
      heading: "Needs attention",
      summary: "{n} need attention",
      allBalanced: "Nothing needs attention",
      allBalancedBody: "Every kiosk and the cage balance for this business date.",
      cageSubject: "Cage",
      exception: "{amount} in payout exceptions.",
      unresolved: "{amount} unresolved on either side.",
      adjustments: "{n} wallet adjustment(s) matched against this kiosk.",
      pending: "Still settling — money outstanding on one side.",
      discrepant: "Cash accepted and credit issued do not agree.",
    },
    table: {
      heading: "All kiosks",
      kiosk: "Kiosk {id}",
      viewDrilldown: "View line by line",
      selectPrompt: "Select a kiosk above to see its line-by-line detail.",
    },
    /**
     * `heading`'s own `StateBadge` is the SECOND, separate verdict — never the day verdict above.
     * `discrepant*`/`stuck*` mirror `money.*`'s honest render and D1a's cash-box row respectively.
     */
    operations: {
      heading: "Operations",
      hint: "Float top-ups, empties, closeouts and reconciles — a separate question from cash accepted vs credit issued above.",
      unattributed:
        "{n} unattributed — authorized by the break-glass PIN, not a signed-in staff member.",
      discrepantHeading: "Discrepant operations",
      discrepantEmpty: "No discrepant operations",
      discrepantEmptyBody: "Every operation's declared and reached amounts agree.",
      stuckHeading: "Stranded operations",
      stuckHint: "Open on any day — not filtered by the business date above.",
      stuckEmpty: "No stranded operations",
      stuckEmptyBody: "Nothing is waiting on a manager to close it.",
      stuckSince: "Stranded since {when}",
      expires: "Expires {when}",
      noExpiry: "No expiry",
      close: "Close",
      closeOnlyManager: "Only a manager can close a stranded operation.",
      noteLabel: "What did you find?",
      notePlaceholder: "e.g. counted the cassette, hardware jam confirmed",
      knowsAmount: "I counted the notes and know the amount",
      amountLabel: "Amount counted (THB)",
      review: "Review",
      closeCancel: "Cancel",
      closeTitle: "Close {kind} — Kiosk {kiosk}",
      closeLabel: "Close operation — Kiosk {kiosk}",
      closeConsequence: "This is recorded as closed_by_staff and no route reopens it.",
      closing: "Closing…",
      closeErrorNotCloseable: "The kiosk may still claim this — it cannot be closed yet.",
      closeErrorScope: "This Casino does not match your session. Refresh and try again.",
      closeErrorCasinoRequired: "Choose a Casino before closing this operation.",
      closeErrorMismatch: "The attested amount does not match the notes. Check the figures.",
      closeErrorInactive: "This Casino or staff account is inactive.",
      closeErrorUpstream: "Could not reach the kiosk service. Try again.",
      closeErrorGeneric: "Could not close this operation. Check the details and try again.",
    },
    drilldown: {
      heading: "Kiosk {kiosk} — line by line",
      empty: "No device activity",
      emptyBody: "This kiosk took no cash events or payout intents on this business date.",
      cashEventsHeading: "Cash events",
      payoutIntentsHeading: "Payout intents",
      device: "Device",
      wallet: "Wallet",
      notLanded: "Not landed yet",
      duplicate: "duplicate",
      authorized: "Authorized",
      dispensed: "Dispensed",
      debit: "Debit",
      refund: "Refund",
    },
  },
  /**
   * Payout-exception resolution on the manager's `/day` workspace — `PayoutExceptionsSection.vue`.
   * A NEW, clearly-scoped block (not interleaved into `day.*` above) on purpose: `docs/OPEN-ITEMS.md`
   * notes a Thai QA pass is running on the main checkout's `th.ts` concurrently, and keeping this
   * addition out of the existing `day.*` keys keeps that merge clean.
   */
  dayExceptions: {
    heading: "Payout exceptions",
    hint: "Every open payout exception, across all days — not filtered by the business date above.",
    errorGeneric: "Could not load payout exceptions. This list may be missing rows — try again.",
    empty: "No open payout exceptions",
    emptyBody: "Every payout exception has been resolved.",
    kiosk: "Kiosk {id}",
    cage: "Cage",
    authorized: "authorized",
    dispensed: "dispensed",
    creditLabel: "Credit to the player's wallet",
    playerId: "Player",
    reasonUnknownDispense: "Unknown dispense — count the cassette.",
    reasonRefundUnresolved: "Refund unresolved — check the vendor's record.",
    reasonDebitUnresolved: "Debit unresolved — check the vendor's record for the debit.",
    reasonUnknown: "No reason recorded",
    reasonUnknownCannotResolve:
      "This exception has no reason recorded, so it cannot be resolved here.",
    loadMoreError: "Could not load the next page. The rows above are still current.",
    retry: "Try again",
    loadMore: "Load more",
    resolve: "Resolve",
    resolveOnlyManager: "Only a manager can resolve a payout exception.",
    review: "Review",
    cancel: "Cancel",
    noteLabel: "What did you find?",
    notePlaceholder: "e.g. counted the cassette, matches the authorized amount",
    attestedDispensedLabel: "Counted in the cassette (THB)",
    attestedDispensedHint: "Between ฿0 and {amount}.",
    attestedDispensedRequired: "Enter what was counted in the cassette.",
    attestedDispensedOutOfRange:
      "Enter a whole number between ฿0 and the authorized amount — it will not be rounded to fit.",
    originalRefundLandedLabel: "Did the vendor's record show the original refund landed?",
    originalRefundLandedYes: "Yes, it landed",
    originalRefundLandedNo: "No, it did not land",
    /** A question about the vendor's record, never the Kiosk's (HANDOFF §5, `debit_unresolved`). */
    originalDebitLandedLabel: "Does the vendor show a {amount} debit for this withdrawal?",
    originalDebitLandedYes: "Yes — the vendor shows the debit",
    originalDebitLandedNo: "No — the vendor shows no debit",
    confirmTitle: "Resolve exception — Kiosk {kiosk}",
    confirmLabel: "Resolve exception — {player}",
    consequence:
      "This credits the wallet for the amount minus what you attested, and cannot be undone from here.",
    consequenceDebitLanded:
      "The player was debited and no notes came out, so this credits the player's wallet the full amount back. It cannot be undone from here.",
    consequenceDebitNotLanded:
      "Nothing moved: this closes the exception without writing to the player's wallet. It cannot be undone from here.",
    confirmPhraseInstruction:
      "Type the credit amount in digits only — no ฿ symbol, no commas — to confirm.",
    submitting: "Resolving…",
    dismiss: "Dismiss",
    resultPending: "Still processing.",
    resultPosted: "Resolved. The credit has posted.",
    resultRefused: "The wallet declined this resolution. Review the details and try again.",
    resultUnresolved:
      "The outcome could not be confirmed. Do not retry — this now appears under stuck cash events for follow-up.",
    resultClosed: "Closed without a wallet write.",
    errorOutOfRange:
      "The counted amount is outside the authorized range. Check the figure and try again.",
    errorAttestationShape:
      "This attestation does not match the exception's own reason. Refresh and try again.",
    errorReasonUnknown: "This exception has no reason recorded and cannot be resolved here.",
    errorNotFound: "This exception could not be found. It may already be resolved.",
    errorScope: "This Casino does not match your session. Refresh and try again.",
    errorCasinoRequired: "Choose a Casino before resolving this exception.",
    errorRace:
      "Another attempt already recorded this exception's outcome. Refresh to see the current state.",
    errorPayoutNotDebited:
      "This payout has no succeeded debit on record, so there is nothing to compensate. Resubmitting will not change that — check the debit before trying again.",
    errorRefundAlreadySucceeded:
      "A refund for this payout has already succeeded. Crediting again would pay the player twice, so this cannot be submitted — refresh the list before doing anything else.",
    errorRefundNotUnresolved:
      "The linked refund is no longer unresolved — its status has changed since this list loaded. Refresh the list rather than resubmitting; these details are out of date.",
    errorDebitNotUnresolved:
      "The wallet no longer records this payout's debit as unresolved — it settled one way or the other since this list loaded. Refresh the list rather than resubmitting; these details are out of date.",
    errorCashEventMismatch:
      "This exception was already resolved with a different attestation than the one just submitted. Resubmitting these numbers will not change that — check what was actually recorded.",
    errorStaffCashConflict:
      "This exception's resolution is already claimed under a different Casino. It cannot be resolved from here — retrying will not help.",
    errorRoleForbidden:
      "Your role no longer permits resolving exceptions. Sign in again to refresh your permissions before trying again.",
    errorInactive: "This Casino or staff account is inactive.",
    errorUpstream: "Could not reach the wallet or kiosk service. Try again.",
    stuckHeading: "Stuck cash events",
    stuckHint: "Open on any day — not filtered by the business date above.",
    cageWithdrawalKind: "Cage withdrawal",
    exceptionResolutionKind: "Exception resolution",
  },
  /**
   * `StrandedPayoutsSection.vue` — `GET /v1/payout/stranded`. Read-only: these are not Payout
   * Exceptions and nothing here resolves them. The age column states the threshold, because the
   * route carries no per-row timestamp.
   */
  dayStranded: {
    heading: "Stranded payouts",
    hint: "Payouts a kiosk never finished after the debit — follow up at the machine. Open on any day, and not resolvable here: the kiosk may still report.",
    onlyManager: "Only a manager can see stranded payouts.",
    error: "Could not load stranded payouts. This is not the same as none being stranded.",
    retry: "Try again",
    empty: "No stranded payouts",
    emptyBody: "Every kiosk payout older than {minutes} minutes has finished.",
    ageAtLeast: "over {minutes} min",
    columns: {
      kiosk: "Kiosk",
      player: "Player",
      amount: "Amount",
      status: "Status",
      age: "Stranded for",
    },
  },
  /**
   * The admin workspace: Casino administration, break-glass entry, and staff. `casino.*` covers
   * `routes/index.vue`'s settings table — the tenancy override (adapter/agentCode) is the one
   * write in it that carries `ConfirmIrreversible`'s heaviest tier, because it "moves no money and
   * yet decides where money lands" (the API's own words on `PATCH /v1/casino/{id}`). `enter.*`
   * covers `components/EnterCasino.vue` — the break-glass mode, gated by a step-up password rather
   * than a confirm dialog, because it is reversible and `leave()` always undoes it. `staff.*`
   * covers `routes/staff.vue`.
   */
  tenancy: {
    heading: "Tenancy",
    subnav: "Tenancy sections",
    staffLink: "Staff",
    casino: {
      heading: "Casinos",
      idColumn: "Casino",
      statusColumn: "Status",
      tenancyColumn: "Adapter · Agent",
      idleColumn: "Idle window",
      manageColumn: "Manage",
      edit: "Edit",
      status: "Status",
      adapter: "Adapter",
      agentCode: "Agent code",
      idleWindowMinutes: "Idle window (minutes)",
      idleWindow: "idle window {n} min",
      save: "Save",
      cancel: "Cancel",
      tenancyWarning:
        "Changing the adapter or agent code changes which wallet every later call for this Casino reaches. Get it wrong and it lands at a Casino that is not this one.",
      confirmTitle: "Change tenancy — {casino}",
      confirmLabel: "Change tenancy",
      consequence:
        "This Casino's wallet calls will route under the agent code below from now on. This moves no money by itself, but decides where every later payment lands.",
      saving: "Saving…",
      pairFrom: "From",
      pairTo: "To",
      frozenNotice:
        "This Casino already has players. Changing this permanently strands their existing wallet mappings — there is no route back from it.",
      retryAcknowledged: "Retry, accepting the stranded mappings",
      errorNotFound: "This Casino could not be found.",
      errorInactive: "This Casino is suspended.",
      errorFrozen:
        "This Casino already has players; the adapter or agent code cannot change without accepting that existing mappings are stranded.",
      errorGeneric: "Could not complete that request. Check the details and try again.",
      emptyHeadline: "No Casinos yet",
      emptyBody: "Use Create Casino above to add the first one.",
      reviewing: "Reviewing this change below — the fields are locked until you confirm or cancel.",
      create: {
        trigger: "Create Casino",
        onlyAdmin: "Only a platform admin can create a Casino.",
        title: "Create a Casino",
        id: "Casino id",
        idHint: "Cannot be changed after creation.",
        name: "Name",
        adapter: "Adapter",
        adapterPlaceholder: "Select an adapter",
        agentCode: "Agent code",
        agentCodeHint:
          "One Agent serves exactly one Casino — getting this wrong is cheap to fix only before the Casino has players.",
        idleWindowMinutes: "Idle window (minutes, optional)",
        submit: "Review Casino",
        cancel: "Cancel",
        confirmTitle: "Create Casino — {name}",
        confirmLabel: "Create {name}",
        consequence:
          "This Casino can accept sessions once a Kiosk is registered to it. The adapter and agent code decide where every later wallet call for this Casino lands.",
        creating: "Creating…",
        errorAlreadyExists: "A Casino with that id already exists.",
        errorAgentPairBound: "Another Casino already holds that adapter and agent code pair.",
        errorUpstream: "Could not reach the play service. Try again.",
        errorGeneric: "Could not complete that request. Check the details and try again.",
        successTitle: "Casino created — {name}",
        successBody:
          "{id} is active. Register a kiosk to it from the Kiosks workspace to start taking sessions.",
        done: "Done",
      },
    },
    enter: {
      heading: "Enter a Casino",
      currentlyActing: "Currently acting at {casino}.",
      noCasinos: "No Casinos to enter yet.",
      pick: "Casino",
      search: "Search Casinos — id or name…",
      countOf: "{n} of {total} Casinos",
      noMatch: "No Casino matches “{q}”.",
      aboutToEnter: "Entering",
      pickHint: "Choose a Casino above to enter.",
      enterNamed: "Enter {casino}",
      pickFirst: "Choose a Casino first",
      password: "Your password",
      entering: "Entering…",
      errorPasswordRequired: "Enter your password to confirm.",
      errorInvalidPassword: "Incorrect password.",
      errorStepUpRequired:
        "For this action your password must be confirmed again. Enter it and retry.",
      errorRateLimited: "This is locked for another {minutes} minutes. It is not your password.",
      errorGeneric: "Could not enter this Casino. Try again.",
      errorLoadingCasinos: "Could not load Casinos. Try again.",
    },
    staff: {
      heading: "Staff",
      partition: "Partition",
      platformAdminPartition: "Platform admins",
      statusColumn: "Status",
      manageColumn: "Manage",
      countTotal: "{n} total",
      countActive: "{n} active",
      countInactive: "{n} suspended",
      roleImmutableHint: "A platform_admin's role cannot be changed.",
      deactivate: "Deactivate",
      activate: "Activate",
      setPassword: "Set password",
      submitPassword: "Set password",
      cancel: "Cancel",
      createHeading: "Add staff",
      username: "Username",
      password: "Password",
      passwordHint: "At least 12 characters.",
      role: "Role",
      newIsPlatformAdmin: "This partition only creates platform_admin accounts.",
      create: "Add staff",
      creating: "Adding…",
      errorAlreadyExists: "That username is already taken in this partition.",
      errorRoleImmutable:
        "A platform_admin's role cannot be changed. Deactivate and create a scoped account instead.",
      errorLastAdmin:
        "This is the last active platform_admin. Activate a second admin before changing this one.",
      errorNotFound: "This staff member could not be found.",
      errorGeneric: "Could not complete that request. Check the details and try again.",
      emptyHeadline: "No staff in this partition",
      emptyBody: "Add the first staff member below.",
    },
  },
  /**
   * The `/activity` workspace: the platform-wide staff attribution trail, `platform_admin` only.
   *
   * **`action.*` is a label map with a raw-string fallback, never an exhaustive enum.** The API
   * types `action` as a bare `string` and `staff_actions` only ever grows — a service that starts
   * recording a new verb must not render a blank cell or a raw i18n key on the one screen that
   * exists to say who did what. `routes/index.vue` resolves each through `te()` and falls back to
   * the raw string, so an unmapped action is still legible and still greppable against the log
   * line it came from.
   *
   * The keys mirror the action strings verbatim, dots and all, as nested objects — that is what
   * makes `te("activity.action.cage.withdrawal")` resolve and what keeps the list here readable
   * beside the services that write it.
   */
  activity: {
    heading: "Activity log",
    filters: {
      casino: "Casino",
      /** The absent filter, not a "platform" one: omitting `casinoId` returns EVERY Casino's rows
       *  and the unscoped ones together. There is no request that narrows to the unscoped rows
       *  alone, so no option here claims to. */
      allCasinos: "All Casinos",
      staffId: "Staff id",
      action: "Action",
      allActions: "All actions",
      from: "From",
      /** Inclusive of the day picked. The API's own `to` is exclusive (`at < to`), so the page
       *  sends the start of the following day — see `use-activity.ts`. */
      to: "To",
    },
    columns: {
      time: "Time",
      staff: "Staff",
      action: "Action",
      casino: "Casino",
      target: "Target",
    },
    /** Stands where a Casino id would, for a row that names no single Casino. */
    platform: "platform",
    loadMore: "Load more",
    empty: "No activity matches these filters",
    emptyBody: "Widen the date range, or clear a filter above.",
    error: "Could not load the activity log. This list may be missing rows — try again.",
    action: {
      /** `*_requested` is written BEFORE the wallet call it describes (review M6) — it records an
       *  attempt, not an outcome, so the copy must not read as if money moved. */
      cage: {
        withdrawal: "Cage withdrawal",
        withdrawal_requested: "Requested a cage withdrawal",
      },
      kiosk: {
        operation: {
          authorize: "Authorized a kiosk operation",
          cancel: "Withdrew a kiosk authorization",
          close: "Closed a stranded operation",
        },
        registered: "Registered a kiosk",
      },
      activation: { issued: "Issued an activation code" },
      credential: { revoked: "Revoked a kiosk credential" },
      payout_exception: {
        resolved: "Resolved a payout exception",
        resolve_requested: "Requested a payout exception resolution",
      },
      report: { signoff: "Signed off a business day" },
      casino: {
        created: "Created a Casino",
        updated: "Updated a Casino",
        break_glass_enter: "Entered a Casino under break-glass",
      },
      staff: {
        created: "Created a staff account",
        create_requested: "Requested a staff account",
        password_reset: "Reset a staff password",
      },
      session: { login: "Signed in" },
    },
  },
  /** Accessible names for landmark sections and lists — read by screen readers, never shown. */
  aria: {
    signoffStatus: "Sign-off status",
    changedFields: "Changed fields",
    operations: "Operations",
    discrepantOperations: "Discrepant operations",
    strandedOperations: "Stranded operations",
    strandedPayouts: "Stranded payouts",
    payoutExceptions: "Payout exceptions",
    kioskDrilldown: "Kiosk drill-down",
    cashEvents: "Cash events",
    payoutIntents: "Payout intents",
    signOff: "Sign off",
    casinos: "Casinos",
    createStaff: "Create staff account",
  },
};

export default en;

/**
 * The message shape every other locale must satisfy. Values are `string`, not literals — the point
 * is that a key present here and missing in Thai is a COMPILE error, not a raw key rendered on a
 * screen an operator is reading.
 */
export type MessageSchema = typeof en;
